import { OpenFeature } from '@openfeature/web-sdk';
import { OpenFeatureSplitProvider } from '@splitsoftware/openfeature-web-split-provider';
import { SplitFactory } from '@splitsoftware/splitio-browserjs';

export const HARNESS_OPENFEATURE_DOMAIN = 'harness';
export const HARNESS_ORG_OPENFEATURE_DOMAIN = 'harnessWithOrgIdTargetingKey';

const ANONYMOUS_TARGETING_KEY = 'anonymous';
const FRONTEND_FEATURE_FLAG_PREFIX = 'frontend_';

// Flags that are not prefixed with 'frontend_' but are needed in Portal
export const NONFRONTEND_FEATURE_FLAG_NAMES = ['backend_menu_shouldAssignMenuToStoreFromOomEvents'];

type SplitFactoryWithDestroy = {
  destroy(): Promise<void>;
};

const harnessProviderState = {
  isInitialized: false,
  isDestroying: false,
  latestInitializeToken: 0,
  splitFactory: undefined as SplitFactoryWithDestroy | undefined,
};

function destroySplitFactory(splitFactory: SplitFactoryWithDestroy): void {
  if (harnessProviderState.isDestroying || harnessProviderState.splitFactory !== splitFactory) {
    return;
  }

  let destroyPromise: Promise<void>;

  try {
    destroyPromise = splitFactory.destroy();
  } catch (error) {
    destroyPromise = Promise.reject(error);
  }

  harnessProviderState.isDestroying = true;

  void destroyPromise
    .catch((error: unknown) => {
      console.error('[FeatureFlags] Failed to destroy Harness Split factory.', error);
    })
    .finally(() => {
      if (harnessProviderState.splitFactory === splitFactory) {
        // Keep initialization blocked until destruction completes so a retry
        // cannot create a second Split SDK instance alongside this one.
        harnessProviderState.splitFactory = undefined;
        harnessProviderState.isDestroying = false;
        harnessProviderState.isInitialized = false;
      }
    });
}

function disableHarnessProvider(warningMessage?: string): void {
  const splitFactory = harnessProviderState.splitFactory;
  if (splitFactory) {
    destroySplitFactory(splitFactory);
  } else {
    harnessProviderState.isInitialized = false;
  }

  harnessProviderState.latestInitializeToken += 1;

  if (warningMessage && process.env.NODE_ENV === 'development') {
    console.warn(warningMessage);
  }
}

export function initializeHarnessFeatureFlags(): void {
  const clientKey = process.env.VITE_SPLIT_OAUTH_KEY;

  if (!clientKey) {
    disableHarnessProvider(
      "[FeatureFlags] Harness feature flags are disabled because 'VITE_SPLIT_OAUTH_KEY' is missing."
    );
    return;
  }

  if (harnessProviderState.isInitialized) {
    return;
  }

  harnessProviderState.isInitialized = true;
  const initializationToken = ++harnessProviderState.latestInitializeToken;

  // Harness FME is backed by the Split SDK, but consumers evaluate flags
  // exclusively through OpenFeature.
  const splitFactory = SplitFactory({
    core: {
      authorizationKey: clientKey,
      key: ANONYMOUS_TARGETING_KEY,
    },
    sync: {
      splitFilters: [
        {
          type: 'byPrefix',
          values: [FRONTEND_FEATURE_FLAG_PREFIX, ...NONFRONTEND_FEATURE_FLAG_NAMES], // technically NONFRONTEND_FEATURE_FLAG_NAMES are not prefixes,
          // but we can't add a byName filter, because split ANDs the filters (instead of ORing them)
        },
      ],
    },
  });
  harnessProviderState.splitFactory = splitFactory;

  const provider = new OpenFeatureSplitProvider(splitFactory);

  void OpenFeature.setProviderAndWait(HARNESS_OPENFEATURE_DOMAIN, provider).catch(
    (error: unknown) => {
      if (initializationToken !== harnessProviderState.latestInitializeToken) {
        return;
      }
      destroySplitFactory(splitFactory);
      console.error('[FeatureFlags] Failed to initialize Harness OpenFeature provider.', error);
    }
  );

  // Shares the same Split factory/client as the domain above; onContextChange
  // re-clients this provider under the org id once the org domain's context is
  // set, without spinning up a second Split SDK instance.
  // Org registration is additive: if it fails, leave the shared factory up so
  // existing user-keyed Harness flags keep working. Org flags fail closed via
  // provider unreadiness / defaults.
  const orgProvider = new OpenFeatureSplitProvider(splitFactory);

  void OpenFeature.setProviderAndWait(HARNESS_ORG_OPENFEATURE_DOMAIN, orgProvider).catch(
    (error: unknown) => {
      if (initializationToken !== harnessProviderState.latestInitializeToken) {
        return;
      }
      console.error(
        '[FeatureFlags] Failed to initialize Harness org-keyed OpenFeature provider.',
        error
      );
    }
  );
}
